import React, { useState, useRef } from 'react';
import {
  X, Send, Loader2, Upload, User, Mail, Phone, MapPin, Briefcase,
  FileText, Linkedin, Globe, CheckCircle, AlertCircle, Sparkles,
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const inputCls = "w-full bg-white/[0.03] border border-white/[0.06] rounded-xl px-3.5 py-2.5 text-white text-sm placeholder-white/25 focus:outline-none focus:border-[#00E5FF]/40 focus:ring-1 focus:ring-[#00E5FF]/10 transition-all";
const labelCls = "text-[10px] tracking-[0.18em] uppercase text-white/40 font-medium mb-1.5 flex items-center gap-1.5";

const VacancyApplicationModal = ({ vacancy, onClose, currentUser }) => {
  const [form, setForm] = useState({
    full_name: currentUser ? `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim() : '',
    email: currentUser?.email || '',
    phone: currentUser?.phone || '',
    roblox_username: currentUser?.roblox_username || '',
    location: currentUser?.location || '',
    years_experience: '',
    linkedin_url: '',
    portfolio_url: '',
    cover_letter: '',
  });
  const [cv, setCv] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const cvInputRef = useRef(null);

  const update = (k, v) => {
    setForm(prev => ({ ...prev, [k]: v }));
    if (errors[k]) setErrors(prev => ({ ...prev, [k]: null }));
  };

  const validate = () => {
    const next = {};
    if (!form.full_name.trim()) next.full_name = 'Required';
    if (!form.email.trim()) next.email = 'Required';
    else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) next.email = 'Enter a valid email';
    if (cv && cv.size > 8 * 1024 * 1024) next.cv = 'CV must be ≤ 8MB';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleCvSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, cv: 'CV must be ≤ 8MB' }));
      return;
    }
    const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain', 'application/rtf'];
    if (!validTypes.includes(file.type)) {
      setErrors(prev => ({ ...prev, cv: 'PDF, DOC, DOCX, RTF, or TXT only' }));
      return;
    }
    setCv(file);
    setErrors(prev => ({ ...prev, cv: null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const data = new FormData();
      data.append('vacancy_id', vacancy.id);
      data.append('vacancy_title', vacancy.title);
      Object.entries(form).forEach(([k, v]) => data.append(k, v || ''));
      if (cv) data.append('cv', cv);
      await axios.post(`${BACKEND_URL}/api/vacancies/apply`, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSubmitted(true);
      toast.success('Application submitted');
      setTimeout(() => onClose(), 2400);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Success view ──
  if (submitted) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md" data-testid="application-success">
        <div className="relative w-full max-w-md bg-[#0A0A12] border border-emerald-500/25 rounded-2xl p-10 text-center overflow-hidden"
          style={{ boxShadow: '0 0 80px rgba(16,185,129,0.15)' }}>
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-emerald-500/[0.06] to-transparent" />
          <div className="relative">
            <div className="w-16 h-16 mx-auto mb-5 rounded-2xl bg-emerald-500/15 border border-emerald-500/25 flex items-center justify-center"
              style={{ boxShadow: '0 0 30px rgba(16,185,129,0.2)' }}>
              <CheckCircle className="w-7 h-7 text-emerald-300" strokeWidth={1.5} />
            </div>
            <h2 className="text-xl font-light text-white tracking-tight mb-2">Application Received</h2>
            <p className="text-white/50 text-sm leading-relaxed mb-6">
              Thanks {form.full_name.split(' ')[0] || 'there'} — we've got your application for <span className="text-emerald-300">{vacancy.title}</span>.
              We'll review it and be in touch shortly.
            </p>
            <p className="text-[10px] tracking-[0.15em] uppercase text-white/25">Check your email for a confirmation soon</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md"
      onClick={onClose} data-testid="application-modal">
      <div onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-2xl max-h-[92vh] overflow-y-auto bg-[#0A0A12] border border-white/10 rounded-2xl"
        style={{ boxShadow: '0 0 80px rgba(0,229,255,0.08)' }}>

        {/* Ambient orbs */}
        <div className="pointer-events-none absolute -top-24 -right-24 w-72 h-72 bg-[#00E5FF]/[0.05] rounded-full blur-[100px]" />
        <div className="pointer-events-none absolute -bottom-24 -left-24 w-72 h-72 bg-[#8A2BE2]/[0.05] rounded-full blur-[100px]" />

        {/* Header */}
        <div className="relative sticky top-0 bg-[#0A0A12]/95 backdrop-blur-md border-b border-white/[0.06] px-6 py-5 flex items-start justify-between z-10">
          <div className="min-w-0 pr-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[9px] tracking-[0.12em] uppercase font-medium bg-[#00E5FF]/[0.08] text-[#00E5FF] border border-[#00E5FF]/25">
                <Briefcase className="w-2.5 h-2.5" /> {vacancy.department || 'Position'}
              </span>
              <span className="text-[10px] tracking-[0.15em] uppercase text-white/30">Application</span>
            </div>
            <h2 className="text-xl font-medium text-white tracking-tight truncate">{vacancy.title}</h2>
            <p className="text-white/40 text-xs mt-1">{vacancy.location} · {vacancy.type}</p>
          </div>
          <button onClick={onClose} data-testid="close-application-modal"
            className="w-9 h-9 rounded-full bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] flex items-center justify-center text-white/60 hover:text-white flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="relative p-6 space-y-5">
          {/* Personal */}
          <SectionHeading icon={User} title="About You" />
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className={labelCls}><User className="w-3 h-3" /> Full Name *</label>
              <input value={form.full_name} onChange={(e) => update('full_name', e.target.value)}
                className={`${inputCls} ${errors.full_name ? 'border-red-500/40' : ''}`} placeholder="Jane Doe" data-testid="app-full-name" />
              {errors.full_name && <p className="text-[10px] text-red-400 mt-1">{errors.full_name}</p>}
            </div>
            <div>
              <label className={labelCls}><Mail className="w-3 h-3" /> Email *</label>
              <input type="email" value={form.email} onChange={(e) => update('email', e.target.value)}
                className={`${inputCls} ${errors.email ? 'border-red-500/40' : ''}`} placeholder="jane@example.com" data-testid="app-email" />
              {errors.email && <p className="text-[10px] text-red-400 mt-1">{errors.email}</p>}
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className={labelCls}><Phone className="w-3 h-3" /> Phone (optional)</label>
              <input value={form.phone} onChange={(e) => update('phone', e.target.value)}
                className={inputCls} placeholder="+44 …" data-testid="app-phone" />
            </div>
            <div>
              <label className={labelCls}><MapPin className="w-3 h-3" /> Location (optional)</label>
              <input value={form.location} onChange={(e) => update('location', e.target.value)}
                className={inputCls} placeholder="City, Country" data-testid="app-location" />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className={labelCls}><Sparkles className="w-3 h-3" /> Roblox Username</label>
              <input value={form.roblox_username} onChange={(e) => update('roblox_username', e.target.value)}
                className={inputCls} placeholder="e.g. StarPlayer_1234" data-testid="app-roblox-username" />
            </div>
            <div>
              <label className={labelCls}><Briefcase className="w-3 h-3" /> Years of Experience</label>
              <select value={form.years_experience} onChange={(e) => update('years_experience', e.target.value)}
                className={inputCls} data-testid="app-years-experience">
                <option value="" className="bg-[#0A0A12]">— Select —</option>
                {['<1 year', '1-2 years', '3-5 years', '5-10 years', '10+ years'].map(x => (
                  <option key={x} value={x} className="bg-[#0A0A12]">{x}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Links */}
          <SectionHeading icon={Globe} title="Online Presence (optional)" />
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className={labelCls}><Linkedin className="w-3 h-3" /> LinkedIn</label>
              <input type="url" value={form.linkedin_url} onChange={(e) => update('linkedin_url', e.target.value)}
                className={inputCls} placeholder="https://linkedin.com/in/…" data-testid="app-linkedin" />
            </div>
            <div>
              <label className={labelCls}><Globe className="w-3 h-3" /> Portfolio / Website</label>
              <input type="url" value={form.portfolio_url} onChange={(e) => update('portfolio_url', e.target.value)}
                className={inputCls} placeholder="https://…" data-testid="app-portfolio" />
            </div>
          </div>

          {/* CV upload */}
          <SectionHeading icon={FileText} title="Your CV" />
          <div className={`rounded-xl border-2 border-dashed p-5 transition-all ${
            cv ? 'border-emerald-500/30 bg-emerald-500/[0.03]' : errors.cv ? 'border-red-500/30 bg-red-500/[0.03]' : 'border-white/10 bg-white/[0.02] hover:border-white/20 hover:bg-white/[0.04]'
          }`}>
            <input type="file" ref={cvInputRef} onChange={handleCvSelect}
              accept=".pdf,.doc,.docx,.rtf,.txt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,application/rtf"
              className="hidden" data-testid="app-cv-input" />
            {cv ? (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/15 border border-emerald-500/25 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-4 h-4 text-emerald-300" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm truncate">{cv.name}</p>
                  <p className="text-white/40 text-[10px]">{(cv.size / 1024).toFixed(1)} KB · Ready to upload</p>
                </div>
                <button type="button" onClick={() => { setCv(null); if (cvInputRef.current) cvInputRef.current.value = ''; }}
                  className="text-white/40 hover:text-red-300 text-[10px] tracking-[0.1em] uppercase transition-colors flex-shrink-0" data-testid="clear-cv-btn">
                  Remove
                </button>
              </div>
            ) : (
              <button type="button" onClick={() => cvInputRef.current?.click()}
                className="w-full flex flex-col items-center gap-2 py-2 group" data-testid="app-cv-upload-trigger">
                <div className="w-10 h-10 rounded-lg bg-white/[0.04] border border-white/[0.08] group-hover:border-[#00E5FF]/30 flex items-center justify-center transition-colors">
                  <Upload className="w-4 h-4 text-white/40 group-hover:text-[#00E5FF] transition-colors" />
                </div>
                <p className="text-white/60 text-sm group-hover:text-white transition-colors">Upload your CV</p>
                <p className="text-white/30 text-[10px] tracking-[0.1em] uppercase">PDF · DOC · DOCX · RTF · TXT · max 8MB</p>
              </button>
            )}
          </div>
          {errors.cv && <p className="text-[10px] text-red-400 -mt-3 flex items-center gap-1.5"><AlertCircle className="w-3 h-3" /> {errors.cv}</p>}

          {/* Cover letter */}
          <SectionHeading icon={FileText} title="Why You? (optional)" />
          <textarea value={form.cover_letter} onChange={(e) => update('cover_letter', e.target.value.slice(0, 4000))}
            rows={5} placeholder="Tell us why you're a fit for this role and what excites you about Starnight Holdings…"
            className={`${inputCls} resize-none`} data-testid="app-cover-letter" maxLength={4000} />
          <div className="flex justify-between text-[10px] text-white/25 -mt-3">
            <span>Max 4000 characters</span>
            <span>{form.cover_letter.length} / 4000</span>
          </div>

          {/* Footer actions */}
          <div className="sticky bottom-0 -mx-6 -mb-6 px-6 py-4 bg-[#0A0A12]/95 backdrop-blur-md border-t border-white/[0.06] flex items-center justify-between gap-3 flex-wrap">
            <p className="text-[10px] text-white/25 flex-1 min-w-0">
              By submitting, you agree to us contacting you about this role.
            </p>
            <div className="flex items-center gap-2">
              <Button type="button" variant="ghost" onClick={onClose}
                className="h-10 px-4 rounded-full text-[11px] tracking-[0.1em] uppercase text-white/50 hover:text-white">
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} data-testid="submit-application-btn"
                className="h-10 px-6 rounded-full text-[11px] tracking-[0.1em] uppercase font-semibold bg-gradient-to-br from-[#00E5FF] to-[#0099CC] hover:opacity-90 text-black shadow-lg shadow-[#00E5FF]/15 disabled:opacity-40">
                {submitting ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Submitting…</> : <><Send className="w-3.5 h-3.5 mr-1.5" /> Submit Application</>}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

const SectionHeading = ({ icon: Icon, title }) => (
  <div className="flex items-center gap-2 pt-1">
    <div className="w-6 h-6 rounded-md bg-[#00E5FF]/[0.06] border border-[#00E5FF]/15 flex items-center justify-center">
      <Icon className="w-3 h-3 text-[#00E5FF]/70" strokeWidth={1.5} />
    </div>
    <h3 className="text-[11px] tracking-[0.2em] uppercase text-white/70 font-medium">{title}</h3>
    <div className="flex-1 h-px bg-gradient-to-r from-white/[0.06] to-transparent" />
  </div>
);

export default VacancyApplicationModal;
